import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const slides = [
    {
        id: 1,
        badge: "Terpercaya & Bersertifikat",
        title: "Antrian Tertata,",
        highlight: "Kesehatan",
        titleEnd: "Terjaga",
        bullets: [
            "Layanan medis bersertifikat nasional",
            "Dokter dan tenaga ahli berpengalaman",
            "Proses mudah dan cepat",
            "Dipandu oleh staf profesional",
        ],
        cta: "Ambil Nomor Antrian",
        image: "/careline-hero.png",
    },
    {
        id: 2,
        badge: "Dokter Berpengalaman",
        title: "Temukan Dokter",
        highlight: "Terbaik",
        titleEnd: "untuk Anda",
        bullets: [
            "Ratusan dokter spesialis tersedia",
            "Profil dan jadwal lengkap",
            "Booking langsung dari aplikasi",
            "Konsultasi tatap muka",
        ],
        cta: "Cari Dokter",
        image: "/careline-hero-2.png",
    },
    {
        id: 3,
        badge: "Poliklinik Lengkap",
        title: "Pilih Poliklinik",
        highlight: "Sesuai",
        titleEnd: "Kebutuhan Anda",
        bullets: [
            "Lebih dari 20 spesialisasi tersedia",
            "Jadwal dokter real-time",
            "Booking online tanpa antri panjang",
            "Rekam medis digital terintegrasi",
        ],
        cta: "Lihat Poliklinik",
        image: "/careline-hero-3.png",
    },
];

const partners = [
    { name: "BPJS", icon: "🏥" },
    { name: "Kemenkes", icon: "🏛️" },
    { name: "IDI", icon: "⚕️" },
    { name: "ISO 9001", icon: "✅" },
    { name: "JCI", icon: "🌐" },
];

// ─── Icons ────────────────────────────────────────────────────────────────────
function CheckIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 18 18"
            fill="none"
            className="flex-shrink-0 mt-0.5"
        >
            <circle cx="9" cy="9" r="9" fill="rgba(255,255,255,0.15)" />
            <path
                d="M5 9.5L7.5 12L13 6.5"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ArrowIcon({ direction }) {
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            {direction === "left" ? (
                <polyline points="15 18 9 12 15 6" />
            ) : (
                <polyline points="9 6 15 12 9 18" />
            )}
        </svg>
    );
}

// ─── Animation Variants ───────────────────────────────────────────────────────
const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const itemVariants = {
    hidden: { opacity: 0, y: 22 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    },
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CareLineHero({ carouselImages }) {
    const [current, setCurrent] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [direction, setDirection] = useState("next");

    const BACKEND_BASE_URL =
        process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_API_BASE_URL?.replace('/api', '') ||
        'http://localhost:8000';

    const resolveImageUrl = (url) => {
        if (!url || typeof url !== "string") return "";
        if (url.startsWith("http") || url.startsWith("data:") || url.startsWith("blob:")) return url;
        const normalized = url.replace(/\\/g, "/");
        return `${BACKEND_BASE_URL}${normalized.startsWith("/") ? "" : "/"}${normalized}`;
    };

    const getDynamicSlides = () => {
        if (!carouselImages || carouselImages.length === 0) {
            return slides;
        }

        return carouselImages.map((img, index) => {
            const originalSlide = slides[index % slides.length];
            return {
                ...originalSlide,
                id: `dynamic-${index}`,
                image: resolveImageUrl(img),
            };
        });
    };

    const activeSlides = getDynamicSlides();

    const goTo = useCallback(
        (index, dir = "next") => {
            if (isAnimating) return;
            setIsAnimating(true);
            setDirection(dir);
            setTimeout(() => {
                setCurrent(index);
                setIsAnimating(false);
            }, 450);
        },
        [isAnimating],
    );

    const next = useCallback(
        () => goTo((current + 1) % activeSlides.length, "next"),
        [current, goTo, activeSlides.length],
    );
    const prev = useCallback(
        () => goTo((current - 1 + activeSlides.length) % activeSlides.length, "prev"),
        [current, goTo, activeSlides.length],
    );

    useEffect(() => {
        const timer = setInterval(next, 5500);
        return () => clearInterval(timer);
    }, [next]);

    const slide = activeSlides[current];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            <section
                className="relative w-full overflow-hidden bg-slate-900"
                style={{ minHeight: "100svh", maxHeight: "900px" }}
            >
                {/* ── Background image (animated) ── */}
                <AnimatePresence mode="wait">
                    <motion.img
                        key={slide.id}
                        src={slide.image}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.02 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        onError={(e) => {
                            e.target.style.opacity = "0";
                        }}
                    />
                </AnimatePresence>

                {/* ── Gradient overlay ── */}
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-cyan-400/35 to-slate-900/20 pointer-events-none" />

                {/* Mobile bottom fade */}
                <div
                    className="absolute inset-0 z-[2] md:hidden"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(15,23,42,0.75) 0%, transparent 50%)",
                    }}
                />

                {/* ── Content ── */}
                <div className="relative z-10 h-full flex items-center min-h-[inherit]">
                    <div className="w-full max-w-[1280px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-[60px] py-16 sm:py-20">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={slide.id}
                                className="w-full max-w-[580px]"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                exit={{
                                    opacity: 0,
                                    y: direction === "next" ? -16 : 16,
                                    transition: { duration: 0.3 },
                                }}
                            >
                                {/* Badge */}
                                <motion.div
                                    variants={itemVariants}
                                    className="inline-flex items-center gap-1.5 bg-cyan-500/[0.18] border border-cyan-400/[0.35] text-cyan-200 text-[10px] sm:text-[11px] font-[DM_Sans] font-semibold px-3 py-[5px] rounded-full uppercase tracking-[0.06em] mb-4 sm:mb-[18px] backdrop-blur-sm"
                                >
                                    <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full inline-block flex-shrink-0" />
                                    {slide.badge}
                                </motion.div>

                                {/* Headline */}
                                <motion.h1
                                    variants={itemVariants}
                                    className="font-[Plus_Jakarta_Sans] font-extrabold leading-[1.1] text-white mb-2 tracking-[-0.02em]"
                                    style={{
                                        fontSize: "clamp(28px, 6vw, 56px)",
                                        textShadow:
                                            "0 2px 20px rgba(0,0,0,0.25)",
                                    }}
                                >
                                    {slide.title}
                                    <br />
                                    <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#a5f3fc] via-[#67e8f9] to-[#22d3ee]">
                                        {slide.highlight}
                                    </em>{" "}
                                    {slide.titleEnd}
                                </motion.h1>

                                {/* Bullets */}
                                <motion.ul
                                    variants={itemVariants}
                                    className="list-none p-0 mt-4 sm:mt-[22px] mb-6 sm:mb-[34px] flex flex-col gap-[9px] sm:gap-[11px]"
                                >
                                    {slide.bullets.map((b, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-2.5 font-[DM_Sans] text-[13px] sm:text-sm text-white/80 font-light leading-snug"
                                        >
                                            <CheckIcon />
                                            {b}
                                        </li>
                                    ))}
                                </motion.ul>

                                {/* CTA + Nav buttons */}
                                <motion.div
                                    variants={itemVariants}
                                    className="flex flex-wrap items-center gap-3 sm:gap-3.5 mb-5 sm:mb-9"
                                >
                                    <a
                                        href="#"
                                        className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white font-[Plus_Jakarta_Sans] font-bold text-[14px] sm:text-[15px] rounded-full no-underline transition-all duration-[250ms] shadow-[0_8px_28px_rgba(6,182,212,0.4)] hover:brightness-110 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(6,182,212,0.55)] whitespace-nowrap"
                                    >
                                        {slide.cta}
                                        <svg
                                            width="15"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="flex-shrink-0"
                                        >
                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                            <polyline points="12 5 19 12 12 19" />
                                        </svg>
                                    </a>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={prev}
                                            aria-label="Slide sebelumnya"
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white bg-white/10 border border-white/20 backdrop-blur-[8px] transition-all duration-[220ms] hover:bg-cyan-500 hover:border-cyan-500 hover:scale-[1.08] cursor-pointer"
                                        >
                                            <ArrowIcon direction="left" />
                                        </button>
                                        <button
                                            onClick={next}
                                            aria-label="Slide berikutnya"
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white bg-white/10 border border-white/20 backdrop-blur-[8px] transition-all duration-[220ms] hover:bg-cyan-500 hover:border-cyan-500 hover:scale-[1.08] cursor-pointer"
                                        >
                                            <ArrowIcon direction="right" />
                                        </button>
                                    </div>
                                </motion.div>

                                {/* Dot indicators */}
                                <motion.div
                                    variants={itemVariants}
                                    className="flex items-center gap-1.5 mb-5 sm:mb-7"
                                >
                                    {activeSlides.map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() =>
                                                goTo(
                                                    i,
                                                    i > current
                                                        ? "next"
                                                        : "prev",
                                                )
                                            }
                                            aria-label={`Slide ${i + 1}`}
                                            className={`h-2 rounded-full border-none cursor-pointer p-0 transition-all duration-300 ${
                                                i === current
                                                    ? "w-7 bg-gradient-to-r from-[#06B6D4] to-[#0891B2]"
                                                    : "w-2 bg-white/30"
                                            }`}
                                        />
                                    ))}
                                </motion.div>

                                {/* Partners bar */}
                                <motion.div
                                    variants={itemVariants}
                                    className="flex items-center bg-white/[0.06] border border-white/10 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 w-fit max-w-full backdrop-blur-[14px] overflow-x-auto no-scrollbar"
                                >
                                    <span className="font-[DM_Sans] text-[8px] sm:text-[9px] font-bold text-white/35 tracking-[0.12em] uppercase pr-3 border-r border-white/[0.12] mr-1 whitespace-nowrap flex-shrink-0">
                                        Mitra
                                    </span>
                                    {partners.map((p, i) => (
                                        <div
                                            key={p.name}
                                            className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-0.5 font-[DM_Sans] text-[11px] sm:text-[12px] font-semibold text-white/55 whitespace-nowrap hover:text-cyan-300 cursor-default flex-shrink-0 transition-colors duration-200 ${
                                                i < partners.length - 1
                                                    ? "border-r border-white/[0.08]"
                                                    : "pr-0"
                                            }`}
                                        >
                                            <span className="text-[12px] sm:text-[13px] leading-none">
                                                {p.icon}
                                            </span>
                                            {p.name}
                                        </div>
                                    ))}
                                </motion.div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* ── Wave separator ── */}
                <div className="absolute bottom-[-1px] left-0 w-full z-20 leading-none">
                    <svg
                        viewBox="0 0 1440 56"
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="block w-full h-14"
                    >
                        <path
                            d="M0,32 C360,56 1080,0 1440,32 L1440,56 L0,56 Z"
                            fill="#ffffff"
                        />
                    </svg>
                </div>
            </section>
        </>
    );
}
